import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowIcon } from '@/src/renderer/components/icons/ArrowIcon'
import { ArrowLeftIcon } from '@/src/renderer/components/icons/ArrowLeftIcon'
import { AddIcon } from '@/src/renderer/components/icons/AddIcon'
import { CommandIcon } from '@/src/renderer/components/icons/CommandIcon'
import { KeyArrowDownIcon, KeyArrowLeftIcon, KeyArrowRightIcon, KeyArrowUpIcon } from '@/src/renderer/components/icons/KeyArrowIcons'
import { OptionKeyIcon } from '@/src/renderer/components/icons/OptionKeyIcon'
import { usePlatform } from '@/src/renderer/hooks/usePlatform'
import type { ButtonShortcut, ButtonVariant } from './Button.types'
import './ShortcutBadge.scss'

/*
  Zeichen, die als Icon kommen statt als Schrift: So tragen sie dieselbe Linie wie ↵ (SCSS,
  `stroke-width`). Als Schriftzeichen kamen sie aus einer Ersatzschrift — kräftiger, je nach Plattform anders.
*/
const GLYPH_ICONS: Record<string, ComponentType> = {
  '↑': KeyArrowUpIcon,
  '↓': KeyArrowDownIcon,
  '←': KeyArrowLeftIcon,
  '→': KeyArrowRightIcon,
  '⌥': OptionKeyIcon,
}

// Single Shortcut Key Component
interface ShortcutKeyProps {
  shortcut: ButtonShortcut
}

const ShortcutKey = ({ shortcut }: ShortcutKeyProps) => {
  const { t } = useTranslation()
  const renderKey = () => {
    switch (shortcut.kind) {
      case 'character': {
        const GlyphIcon = shortcut.text ? GLYPH_ICONS[shortcut.text] : undefined
        if (GlyphIcon) {
          return (
            <span className="button__shortcut-key button__shortcut-key--icon">
              <GlyphIcon />
            </span>
          )
        }
        return (
          <span className="button__shortcut-key button__shortcut-key--character">
            {shortcut.text || ''}
          </span>
        )
      }
      case 'return':
        return (
          <span className="button__shortcut-key button__shortcut-key--icon">
            <ArrowIcon />
          </span>
        )
      case 'add':
        return (
          <span className="button__shortcut-key button__shortcut-key--icon">
            <AddIcon />
          </span>
        )
      case 'back':
        return (
          <span className="button__shortcut-key button__shortcut-key--icon">
            <ArrowLeftIcon />
          </span>
        )
      case 'click':
        return (
          <span className="button__shortcut-key button__shortcut-key--word">
            {t('common.shortcutClick')}
          </span>
        )
      default:
        return null
    }
  }

  return renderKey()
}

// ShortcutBadge Component
export interface ShortcutBadgeProps {
  shortcut: ButtonShortcut | ButtonShortcut[] | undefined
  /**
   * Farb-Kontext: die Button-Variante, auf der das Badge sitzt, `beige` oder `tooltip` (dunkler Tooltip-Grund).
   * `outline` zeichnet die Tasten nur als Umriss in der Textfarbe der Umgebung. `subtle` ist die graue
   * Taste für frei stehende Hinweise (Fußleisten): hellere Fläche und Schrift als im Button.
   */
  variant: ButtonVariant | 'beige' | 'tooltip' | 'subtle'
}

/**
 * Der Modifier für `mod`: ⌘ auf dem Mac, Strg/Ctrl sonst. Eigene Komponente, damit nur Kürzel mit `mod`
 * die Plattform abfragen — alle anderen Badges (und die Tests, die `lib/platform` nur teilweise
 * nachbilden) bleiben davon unberührt.
 */
const PlatformModifierKey = ({ ctrlText }: { ctrlText: string }) => {
  const { isMac } = usePlatform()
  return isMac ? (
    <span className="button__shortcut-key button__shortcut-key--modifier button__shortcut-key--icon">
      <CommandIcon />
    </span>
  ) : (
    <span className="button__shortcut-key button__shortcut-key--modifier">{ctrlText}</span>
  )
}

export const ShortcutBadge = ({ shortcut, variant }: ShortcutBadgeProps) => {
  const { i18n } = useTranslation()

  if (!shortcut) return null

  const onDark = variant === 'primary' || variant === 'destructive'

  let badgeClass = `button__shortcut-badge`
  if (variant === 'beige' || variant === 'tooltip' || variant === 'outline' || variant === 'subtle') {
    badgeClass += ` button__shortcut-badge--${variant}`
  } else {
    badgeClass += ` button__shortcut-badge--${onDark ? 'on-dark' : 'on-light'}`
  }

  // Handle array of shortcuts
  if (Array.isArray(shortcut)) {
    return (
      <div className={badgeClass}>
        {shortcut.map((s, index) => (
          <ShortcutKey key={index} shortcut={s} />
        ))}
      </div>
    )
  }

  // Handle single shortcut with optional modifier
  const ctrlText = i18n?.language === 'de' ? 'Strg' : 'Ctrl'

  // `mod` folgt der Plattform; `cmd`/`ctrl` legen sie fest.
  const platformModifier = shortcut.mod && !shortcut.cmd && !shortcut.ctrl
  const showCmd = shortcut.cmd
  const showCtrl = !showCmd && shortcut.ctrl

  return (
    <div className={badgeClass}>
      {platformModifier && <PlatformModifierKey ctrlText={ctrlText} />}
      {showCmd && (
        <span className="button__shortcut-key button__shortcut-key--modifier button__shortcut-key--icon">
          <CommandIcon />
        </span>
      )}
      {showCtrl && (
        <span className="button__shortcut-key button__shortcut-key--modifier">{ctrlText}</span>
      )}
      {shortcut.shift && (
        <span className="button__shortcut-key button__shortcut-key--modifier">⇧</span>
      )}
      <ShortcutKey shortcut={shortcut} />
    </div>
  )
}
